import React, { useEffect, useState } from 'react';
import { Icon, Modal, Title, Tooltip, Banner } from '@gerdau/hefesto';
import { RcFile } from 'antd/es/upload/interface';
import { useDispatch, useSelector } from 'react-redux';
import CancelUploadFilesModal from '../../../../../../components/CancelUploadFilesModal';
import { FabricationProjectsService } from '../../../../../../services/FabricationProjects/index';

import { loadMockups } from '../../../../../../store/ducks/fabrication-mockups/actions';
import { ApplicationState } from '../../../../../../store/index';
import EditProjectForm, {
  IEditProjectForm
} from '../../EditProjectForm/Facelift';
import {
  NewProjectContainer,
  SendProjectBody,
  SendProjectConfig,
  Subtitle,
  UploadedFilesList,
  BannerContainer
} from './styles';
import { projectPavementsNeedRange } from '../../../../../../utils/fabricationHelper';
import { AxiosError } from 'axios';
import { addToast } from '../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../store/ducks/toast/types';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';

interface INewProjectProps {
  contractNumber: string;
  uploadedFiles: RcFile[];
  closeModal: (
    uploadedProjects: number,
    unsendedProjectsFileNames: string[]
  ) => void;
  handleExistingProject: (filename: string) => void;
}

interface IUploadProjectState extends IEditProjectForm {
  file: RcFile;
}

const NewProject: React.FC<INewProjectProps> = ({
  contractNumber,
  uploadedFiles,
  closeModal,
  handleExistingProject
}) => {
  const dispatch = useDispatch();

  const isSizeGreaterThanMaximumSize = (file: RcFile): boolean => {
    const MAX_FILE_SIZE = 20000;
    const fileSizeKiloBytes = file.size / 1024;
    return fileSizeKiloBytes > MAX_FILE_SIZE;
  };

  const getNextAvailableProjectIdx = (currentIdx?: number): number => {
    let availableIdx = 0;

    if (currentIdx !== undefined) {
      const index = uploadedFiles.findIndex(
        (uf, idx) => !isSizeGreaterThanMaximumSize(uf) && idx > currentIdx
      );
      if (index !== -1) {
        availableIdx = index;
      } else {
        availableIdx = currentIdx;
      }
    } else {
      const index = uploadedFiles.findIndex(
        (uf) => !isSizeGreaterThanMaximumSize(uf)
      );
      if (index !== -1) {
        availableIdx = index;
      }
    }
    return availableIdx;
  };
  const [currentProjectIdx, setCurrentProjectIdx] = useState(
    getNextAvailableProjectIdx()
  );
  const [projectsUploadedSuccess, setProjectsUploadedSuccess] = useState<
    number[]
  >([]);
  const [projectsUploadedError, setProjectsUploadedError] = useState<
    { idx: number; error?: string }[]
  >([]);
  const [isOpenBanner, setIsOpenBanner] = useState(true);

  const mockupState = useSelector(
    (appState: ApplicationState) => appState.fabricationMockups
  );

  const [isLoading, setIsloading] = useState(false);
  const [canShowCancelUploadFilesModal, setCanShowCancelUploadFilesModal] =
    useState(false);

  const handleLoadMockups = () => {
    dispatch(loadMockups(contractNumber));
  };

  useEffect(() => {
    if (
      mockupState.contractNumber !== contractNumber &&
      !mockupState.loading &&
      !mockupState.error
    ) {
      handleLoadMockups();
    }
  });

  const getInitialState = (): IUploadProjectState[] => {
    return uploadedFiles.map((uf) => {
      return {
        file: uf
      };
    });
  };
  const [uploadProjectState, setUploadProjectState] = useState<
    IUploadProjectState[]
  >(getInitialState());

  const removeCurrentProjectIdxFromProjectsUploadedError = () => {
    if (projectsUploadedError.find((pue) => pue.idx === currentProjectIdx)) {
      setProjectsUploadedError(
        projectsUploadedError.filter((p) => p.idx !== currentProjectIdx)
      );
    }
  };

  const sendSelectedProject = () => {
    const { file, pavements, structures, revision, mockupId } =
      uploadProjectState[currentProjectIdx];
    if (!pavements || !structures || !revision) {
      return;
    }
    setIsloading(true);

    FabricationProjectsService.uploadProject(
      contractNumber,
      structures,
      revision,
      file,
      pavements
    )
      .then(() => {
        removeCurrentProjectIdxFromProjectsUploadedError();

        const selectedMockup = mockupState.mockups.find(
          (m) => m.id === mockupId
        );
        const nomeMaquete = selectedMockup?.name || '';
        const pavimentoVinculado =
          selectedMockup?.pavements
            .filter((p) => pavements.includes(p.id as number))
            .map((p) => p.name)
            .join(',') || '';
        dispatch(
          sendTagging({
            screenName: 'LG:MaquetesEProjetos:Maquetes:EnviarProjeto',
            itemName: 'sucess:projeto_enviado_com_sucesso',
            event: 'success',
            itemDescription:
              'maquetesEProjetos_maquetes_enviarProjeto_projetoEnviadoComSucesso',
            nonInteraction: true,
            extraData: {
              nome_maquete: nomeMaquete,
              id_maquete: `${contractNumber}_${nomeMaquete}`,
              estrutura_projeto: structures.join(','),
              pavimento_vinculado: pavimentoVinculado,
              numero_revisao: revision,
              tipo_arquivo: `.${file.name.split('.').pop() || ''}`
            }
          })
        );

        setProjectsUploadedSuccess([
          ...projectsUploadedSuccess,
          currentProjectIdx
        ]);
        setIsloading(false);
        if (currentProjectIdx + 1 < uploadProjectState.length) {
          setCurrentProjectIdx(getNextAvailableProjectIdx(currentProjectIdx));
        }
      })
      .catch(
        (
          err: AxiosError<{
            status: string;
            errors: {
              project?: string;
              file?: string;
              [key: string]: string | undefined;
            };
          }>
        ) => {
          setIsloading(false);

          let message: string | undefined;
          if (err.response?.data?.status === '400') {
            const messages: string[] = Object.values(
              err.response?.data?.errors || {}
            ).filter((value): value is string => value !== undefined);
            if (messages.length > 0) {
              message = messages[0].concat(
                '. Deseja selecionar o projeto existente?'
              );
              dispatch(
                addToast({
                  type: EToastTypes.WARNING,
                  message,
                  labelActionOne: 'Sim',
                  labelActionTwo: 'Não',
                  handleClickActionOne: () => onConfirmExistingProject()
                })
              );
            }
          }

          const newProjectsUploadedError = [
            ...projectsUploadedError.filter((p) => p.idx !== currentProjectIdx)
          ];

          newProjectsUploadedError.push({
            idx: currentProjectIdx,
            error:
              err.response?.data?.errors?.file ||
              message ||
              'Falha ao enviar arquivo. Tente novamente.'
          });

          setProjectsUploadedError(newProjectsUploadedError);
        }
      );
  };

  const isProjectFilled = (): boolean => {
    const { mockupId, pavements, revision, structures } =
      uploadProjectState[currentProjectIdx];

    const isPavementRangeNeeded = projectPavementsNeedRange(structures || []);

    return (
      !!mockupId &&
      structures !== undefined &&
      pavements !== undefined &&
      structures.length > 0 &&
      (isPavementRangeNeeded ? pavements.length > 1 : pavements.length > 0) &&
      !!revision
    );
  };

  const getIcon = (idx: number) => {
    if (projectsUploadedSuccess.includes(idx)) {
      return (
        <Tooltip title="Arquivo enviado">
          <div>
            <Icon variant="success" option="Check" />
          </div>
        </Tooltip>
      );
    } else if (isSizeGreaterThanMaximumSize(uploadProjectState[idx].file)) {
      return (
        <Tooltip title="Não é permitido envio de arquivo maior que 20mb">
          <div>
            <Icon variant="danger" option="Alert" />
          </div>
        </Tooltip>
      );
    } else if (projectsUploadedError.find((pue) => pue.idx === idx)) {
      const pue = projectsUploadedError.find((pue) => pue.idx === idx);
      return (
        <Tooltip title={'Tente novamente.'}>
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {pue?.error}
            <Icon variant="danger" option="Alert" />
          </div>
        </Tooltip>
      );
    }
  };

  const getUnsendedProjectsFileNames = (projectExists = false): string[] => {
    const unsendedProjectsFileNames: string[] = [];
    uploadedFiles.forEach((uf, idx) => {
      if (
        (isSizeGreaterThanMaximumSize(uf) && !projectExists) ||
        (!projectsUploadedSuccess.includes(idx) && !projectExists)
      ) {
        unsendedProjectsFileNames.push(uf.name);
      }
    });

    return unsendedProjectsFileNames;
  };

  const onCloseModal = () => {
    closeModal(projectsUploadedSuccess.length, getUnsendedProjectsFileNames());
  };

  const onConfirmExistingProject = () => {
    const { file, mockupId } = uploadProjectState[currentProjectIdx];
    closeModal(
      projectsUploadedSuccess.length,
      getUnsendedProjectsFileNames(true)
    );
    if (mockupId) {
      handleExistingProject(file.name);
    }
  };

  return (
    <>
      <Modal
        onClose={onCloseModal}
        modalSize="lg"
        buttons={
          projectsUploadedSuccess.length === uploadedFiles.length
            ? {
                right: {
                  name: 'close',
                  children: 'Fechar',
                  onClick: onCloseModal
                }
              }
            : {
                left: {
                  name: 'cancel',
                  children: 'Cancelar',
                  onClick: () => setCanShowCancelUploadFilesModal(true),
                  disabled: isLoading
                },
                right: {
                  name: 'send-file',
                  onClick: sendSelectedProject,
                  children: 'Concluir envio',
                  disabled:
                    isLoading ||
                    mockupState.loading ||
                    projectsUploadedSuccess.includes(currentProjectIdx) ||
                    isSizeGreaterThanMaximumSize(
                      uploadProjectState[currentProjectIdx].file
                    ) ||
                    !isProjectFilled(),
                  loading: isLoading
                }
              }
        }
      >
        <NewProjectContainer>
          <SendProjectBody>
            <UploadedFilesList>
              {uploadProjectState.map((ups, idx) => {
                const selected = currentProjectIdx === idx;
                return (
                  <div
                    key={idx}
                    className={`file${selected ? ' selected' : ''}`}
                    onClick={() => setCurrentProjectIdx(idx)}
                  >
                    <span
                      className="filename"
                      title={ups.file.name}
                      style={{ lineBreak: 'anywhere' }}
                    >
                      {ups.file.name}
                      {projectsUploadedError.find((f) => f.idx === idx)
                        ?.error || ''}
                    </span>
                    <span className="icon">{getIcon(idx)}</span>
                  </div>
                );
              })}
            </UploadedFilesList>

            <SendProjectConfig>
              <Title level={2}>Envio de arquivos</Title>
              <Subtitle>
                <span>
                  Para facilitar a gestão de envio de novos projetos, insira
                  informações sobre seus arquivos
                </span>
                <span>*Campos com preechimento obrigatório</span>
              </Subtitle>
              <EditProjectForm
                projectForm={uploadProjectState[currentProjectIdx]}
                mockupState={mockupState}
                updateProjectform={(updatedState) => {
                  const newState = [...uploadProjectState];
                  newState[currentProjectIdx] = {
                    ...newState[currentProjectIdx],
                    ...updatedState
                  };
                  setUploadProjectState(newState);
                }}
                canUpdateFields={
                  !isLoading &&
                  !mockupState.loading &&
                  !projectsUploadedSuccess.includes(currentProjectIdx) &&
                  !isSizeGreaterThanMaximumSize(
                    uploadProjectState[currentProjectIdx].file
                  )
                }
              />
            </SendProjectConfig>
          </SendProjectBody>
        </NewProjectContainer>
        {!mockupState.loading &&
          mockupState.mockups.length === 0 &&
          isOpenBanner && (
            <BannerContainer>
              <Banner
                bannerWithIcon
                handleActionOne={() => setIsOpenBanner(false)}
                labelActionOne="Fechar"
                text="Para fazer upload de projetos, é preciso cadastrar uma maquete."
                variant="warning"
              />
            </BannerContainer>
          )}
      </Modal>

      {canShowCancelUploadFilesModal && (
        <CancelUploadFilesModal
          onCancelClick={onCloseModal}
          onContinueClick={() => setCanShowCancelUploadFilesModal(false)}
        />
      )}
    </>
  );
};

export default NewProject;
